import React, { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserPlus, Loader2 } from 'lucide-react';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useRecaptchaVerification } from '../hooks/useRecaptcha';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';

const registerSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters long'),
});

type RegisterFormData = z.infer<typeof registerSchema>;

function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteParam = searchParams.get('invite');
  const invitationToken = searchParams.get('invitation');
  const { signUp } = useAuth();
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const { verifyRecaptcha, verifying, error: recaptchaError } = useRecaptchaVerification();

  const { register, handleSubmit, formState: { errors }, watch } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  // Check if invitation token is valid
  useEffect(() => {
    const checkInvitation = async () => {
      if (!invitationToken) return;

      try {
        const { data, error } = await supabase
          .from('invitations')
          .select('*')
          .eq('invitation_token', invitationToken)
          .eq('status', 'pending')
          .maybeSingle();

        if (error || !data) {
          setError('Invalid or expired invitation');
        }
      } catch (err) {
        setError('Error validating invitation');
      }
    };

    checkInvitation();
  }, [invitationToken]);

  const onSubmit = async (data: RegisterFormData) => {
    if (loading || verifying) return; // Prevent multiple submissions
    
    setError('');
    setLoading(true);

    try {
      // Verify reCAPTCHA first
      const isVerified = await verifyRecaptcha();
      
      if (!isVerified) {
        setError(recaptchaError || 'reCAPTCHA verification failed. Please try again.');
        setLoading(false);
        return;
      }
      
      // Proceed with sign up
      const { data: authData, error: signUpError } = await signUp(data.email, data.password);
      
      if (signUpError) {
        if (signUpError.message.includes('user_already_exists')) {
          setError(t('auth.register.accountExists'));
        } else {
          setError('Failed to create account. Please try again.');
        }
        return;
      }
      
      // If there's an invitation token, store it in localStorage to process after profile creation
      if (invitationToken) {
        localStorage.setItem('pendingInvitation', invitationToken);
      }
      
      navigate('/create-profile');
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('user_already_exists')) {
          setError(t('auth.register.accountExists'));
        } else {
          setError('Failed to create account. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 pb-8 md:py-12">
      <div className="panel w-full max-w-md">
        <h1 className="font-tilt-warp text-3xl text-light text-center mb-8">{t('auth.register.title')}</h1>
        
        <div className="mb-8 px-2">
          <p className="text-light/80 leading-relaxed">
            {t('auth.register.introduction')}
          </p>
        </div>
        
        {error && (
          <div className="bg-red-500/10 text-red-500 p-4 rounded-lg mb-6 text-center">
            {error}
          </div>
        )}
        
        {invitationToken && !error && (
          <div className="bg-green-500/10 text-green-500 p-4 rounded-lg mb-6 text-center">
            {t('auth.register.invitationMessage')}
          </div>
        )}
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-light/80 mb-2">
              {t('auth.register.emailLabel')}
            </label>
            <input
              type="email"
              id="email"
              {...register('email')}
              className="block w-full h-12 rounded-lg bg-dark border border-gray-custom/20 px-4 text-light focus:border-secondary/40 focus:outline-none transition-colors"
              disabled={loading || verifying}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-light/80 mb-2">
              {t('auth.register.passwordLabel')}
            </label>
            <input
              type="password"
              id="password"
              {...register('password')}
              className="block w-full h-12 rounded-lg bg-dark border border-gray-custom/20 px-4 text-light focus:border-secondary/40 focus:outline-none transition-colors"
              disabled={loading || verifying}
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-500">{t('auth.register.passwordRequirements')}</p>
            )}
          </div>

          <button
            type="submit"
            className="btn-secondary w-full h-12 flex items-center justify-center"
            disabled={loading || verifying}
          >
            {loading || verifying ? (
              <span className="flex items-center space-x-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{t('auth.register.creatingAccount')}</span>
              </span>
            ) : (
              t('auth.register.signUpButton')
            )}
          </button>
        </form>

        <div className="relative my-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-custom/20"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-[#1a1a1a] text-light/40">{t('auth.register.orDivider')}</span>
          </div>
        </div>

        <GoogleSignInButton mode="signup" />

        <p className="mt-8 text-center text-light/60">
          {t('auth.register.haveAccount')}{' '}
          <Link to="/login" className="text-secondary hover:text-secondary/80 transition-colors">
            {t('auth.register.signIn')}
          </Link>
        </p>
      </div>
    </div>
  );
}

export default Register;